import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  PlusIcon,
  TableIcon,
  Trash2Icon,
} from "lucide-react";
import {
  AttributeCodeVisibilityEnum,
  AttributeKindEnum,
  CensoredProjectionEnum,
  type ExternalTestPartnerId,
  type FoodNutrientTypeId,
  type TestAttributeCodeAliasId,
  type TestAttributeCodeRowKeyId,
} from "@brewbase/db/types";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { SimpleSelect } from "@/components/ui/simple-select";
import { RequireCapability } from "@/components/require-capability";
import { ConfirmActionDialog } from "@/components/confirm-action-dialog";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { formatDate } from "@/lib/date";
import { cn } from "@/lib/utils";

/**
 * The results catalogue.
 *
 * A result is what makes an external lab's histamine and our own histamine the
 * same quantity: templates bind to it, lab parameter names map onto it, and
 * every formula, spec and chart downstream addresses it by its code.
 *
 * Underneath, every code is private or public. The page shows only the public
 * ones — the catalogue. A private code is a template's own formula name,
 * registered so the FK holds, and claims nothing; it is never listed here.
 * Adding a code to the catalogue defines it, and refuses when a template
 * already bound to the code measures something the definition does not accept.
 *
 * This is configuration, so rows are read-only at rest: editing is a deliberate
 * step per row, and a change to what a result *is* (kind, units, how a censored
 * value counts) shows what it touches before it lands.
 */

export const Route = createFileRoute("/analytics/catalogue")({
  component: () => (
    <RequireCapability cap="test-attribute-code:edit">
      <ResultsCatalogue />
    </RequireCapability>
  ),
});

type Entry = RouterOutputs["tests"]["attributeCodes"]["list"][number];

const KIND_OPTIONS = [
  { value: AttributeKindEnum.SINGLE_NUMBER, label: "Number" },
  { value: AttributeKindEnum.TEXT, label: "Text" },
  { value: AttributeKindEnum.CHECK, label: "Check" },
  { value: AttributeKindEnum.TIMESTAMP, label: "Timestamp" },
  { value: AttributeKindEnum.DATESTAMP, label: "Date" },
  { value: AttributeKindEnum.TABULAR_DATA, label: "Profile table" },
];

const PROJECTION_OPTIONS = [
  { value: CensoredProjectionEnum.NULL_VALUE, label: "no value" },
  { value: CensoredProjectionEnum.ZERO, label: "zero" },
  { value: CensoredProjectionEnum.LIMIT, label: "the limit" },
];

const kindLabel = (kind: AttributeKindEnum) =>
  KIND_OPTIONS.find((o) => o.value === kind)?.label ?? kind;
const projectionLabel = (p: CensoredProjectionEnum) =>
  PROJECTION_OPTIONS.find((o) => o.value === p)?.label ?? p;

function parseUnits(text: string): string[] {
  return text
    .split(/[,|]/)
    .map((u) => u.trim())
    .filter((u, i, all) => u !== "" && all.indexOf(u) === i);
}

/** One line: who touched the row last and when. */
function Provenance({ entry }: { entry: { updated_at: Date; last_updated_by_name: string | null } }) {
  return (
    <span className="text-xs text-olive-9 whitespace-nowrap">
      {entry.last_updated_by_name ? `${entry.last_updated_by_name}, ` : ""}
      {formatDate(entry.updated_at)}
    </span>
  );
}

/** "3 templates · 120 results · 4 lab names" — what depends on a result. */
function usageSummary(entry: Entry): string {
  const parts: string[] = [];
  if (entry.template_count) parts.push(`${entry.template_count} template${entry.template_count === 1 ? "" : "s"}`);
  if (entry.result_count) parts.push(`${entry.result_count} result${entry.result_count === 1 ? "" : "s"}`);
  if (entry.alias_count) parts.push(`${entry.alias_count} lab name${entry.alias_count === 1 ? "" : "s"}`);
  return parts.length ? parts.join(" · ") : "unused";
}

function ResultsCatalogue() {
  const utils = trpc.useUtils();
  const canDefine = useCan("test-attribute-code:publish");
  const [search, setSearch] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: codes, isLoading } = trpc.tests.attributeCodes.list.useQuery({
    includeArchived,
    visibility: AttributeCodeVisibilityEnum.PUBLIC,
  });

  const { data: nutrientTypes = [] } = trpc.food.nutrientTypes.list.useQuery();
  const nutrientName = new Map(nutrientTypes.map((t) => [t.id, t.name]));

  const invalidate = () => utils.tests.attributeCodes.list.invalidate();

  const archiveMutation = trpc.tests.attributeCodes.setArchived.useMutation({
    onSuccess: (_, vars) => {
      toast.success(vars.archived ? `${vars.code} archived` : `${vars.code} restored`);
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return codes ?? [];
    return (codes ?? []).filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        (c.category ?? "").toLowerCase().includes(q),
    );
  }, [codes, search]);

  if (isLoading) {
    return <div className="px-4 py-6 text-sm text-olive-9">Loading…</div>;
  }

  return (
    <div className="flex flex-col grow min-h-0 gap-4 px-4 py-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-medium">Results catalogue</h2>
          <p className="text-sm text-olive-11 max-w-3xl">
            Everything a test can report, defined once: a name, the units it
            accepts, and how each lab spells it. Templates bind to a catalogued
            result and lab reports map onto it, so a change here changes what
            existing measurements mean.
          </p>
        </div>
        {canDefine && !creating && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <PlusIcon />
            Add to catalogue
          </Button>
        )}
      </div>

      {creating && (
        <div className="border border-olive-6 bg-olive-1 p-3">
          <DefineForm
            onDone={() => {
              setCreating(false);
              invalidate();
            }}
          />
        </div>
      )}

      <div className="flex items-center gap-3">
        <Input
          placeholder="Search name, code or category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <label className="flex items-center gap-1.5 text-sm text-olive-11">
          <Checkbox
            checked={includeArchived}
            onCheckedChange={(v) => setIncludeArchived(v === true)}
          />
          Show archived
        </label>
        <span className="ml-auto text-xs text-olive-9">
          {filtered.length} of {codes?.length ?? 0}
        </span>
      </div>

      <div className="border border-olive-6">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-olive-2">
              <Th className="w-6" />
              <Th>Name</Th>
              <Th className="w-40" title="Typed into formulas">
                Code
              </Th>
              <Th className="w-24">Kind</Th>
              <Th className="w-36" title="Accepted units, first is primary">
                Units
              </Th>
              <Th className="w-32">Category</Th>
              <Th
                className="w-24"
                title="What a '< 0.010' result counts as when a single number is needed"
              >
                Below limit
              </Th>
              <Th className="w-32" title="A Farmless batch's lab result for this is its nutrition">
                Food nutrient
              </Th>
              <Th className="w-44">Used by</Th>
              <Th className="w-32">Changed</Th>
              <Th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((entry) => {
              const isProfile = entry.kind === AttributeKindEnum.TABULAR_DATA;
              const isEditing = editing === entry.code;
              return (
                <Fragment key={entry.code}>
                  <tr
                    className={cn(
                      "border-b border-olive-4",
                      entry.archived_at && "opacity-50",
                      isEditing && "bg-olive-2",
                    )}
                  >
                    <td className="p-1 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          setExpanded((k) => (k === entry.code ? null : entry.code))
                        }
                        className="text-olive-9 hover:text-olive-12"
                        title="Lab names and rows"
                      >
                        {expanded === entry.code ? (
                          <ChevronDownIcon className="size-3.5" />
                        ) : (
                          <ChevronRightIcon className="size-3.5" />
                        )}
                      </button>
                    </td>
                    <td className="px-2 py-1 text-olive-12">
                      <span className="flex items-center gap-1.5">
                        {isProfile && <TableIcon className="size-3.5 shrink-0 text-olive-9" />}
                        {entry.name}
                      </span>
                    </td>
                    <td className="px-2 py-1 font-mono text-xs text-olive-11">{entry.code}</td>
                    <td className="px-2 py-1">{kindLabel(entry.kind)}</td>
                    <td className="px-2 py-1 font-mono text-xs">{entry.units.join(", ")}</td>
                    <td className="px-2 py-1">{entry.category ?? ""}</td>
                    <td className="px-2 py-1 text-olive-11">
                      {entry.kind === AttributeKindEnum.SINGLE_NUMBER
                        ? projectionLabel(entry.censored_projection)
                        : ""}
                    </td>
                    <td className="px-2 py-1">
                      {entry.food_nutrient_type_id !== null ? nutrientName.get(entry.food_nutrient_type_id) ?? "" : ""}
                    </td>
                    <td className="px-2 py-1 text-xs text-olive-11">{usageSummary(entry)}</td>
                    <td className="px-2 py-1">
                      <Provenance entry={entry} />
                    </td>
                    <td className="p-1">
                      <div className="flex items-center justify-end gap-0.5">
                        {!entry.archived_at && !isEditing && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing(entry.code)}
                            title="Edit"
                          >
                            <PencilIcon />
                          </Button>
                        )}
                        {entry.archived_at ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            title="Restore"
                            onClick={() =>
                              archiveMutation.mutate({ code: entry.code, archived: false })
                            }
                          >
                            <ArchiveRestoreIcon className="size-4" />
                          </Button>
                        ) : (
                          <ArchiveButton
                            entry={entry}
                            onConfirm={() =>
                              archiveMutation.mutate({ code: entry.code, archived: true })
                            }
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr className="border-b border-olive-6">
                      <td />
                      <td colSpan={10} className="p-3 bg-olive-2">
                        <EditForm
                          entry={entry}
                          onDone={() => {
                            setEditing(null);
                            invalidate();
                          }}
                        />
                      </td>
                    </tr>
                  )}
                  {expanded === entry.code && (
                    <tr className="border-b border-olive-6">
                      <td />
                      <td colSpan={10} className="p-2 bg-olive-1">
                        <div className="flex flex-col gap-3">
                          {isProfile && <RowKeyList code={entry.code} />}
                          <AliasList code={entry.code} isProfile={isProfile} />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && (
        <p className="text-sm text-olive-9">No results match.</p>
      )}
    </div>
  );
}

function ArchiveButton({ entry, onConfirm }: { entry: Entry; onConfirm: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="icon" variant="ghost" title="Archive" onClick={() => setOpen(true)}>
        <ArchiveIcon className="size-4" />
      </Button>
      <ConfirmActionDialog
        open={open}
        onOpenChange={setOpen}
        title={`Archive ${entry.name}?`}
        description="An archived result keeps every measurement already recorded, but is no longer offered when mapping a report or binding a template attribute."
        confirmLabel="Archive"
        onConfirm={() => {
          setOpen(false);
          onConfirm();
        }}
      >
        <UsageList entry={entry} />
      </ConfirmActionDialog>
    </>
  );
}

function UsageList({ entry }: { entry: Entry }) {
  return (
    <div className="text-sm text-olive-11">
      <span className="font-mono text-xs text-olive-12">{entry.code}</span> is used by{" "}
      {usageSummary(entry)}.
    </div>
  );
}

/** The fields that define a result; shared by edit, define and new. */
interface Definition {
  name: string;
  kind: AttributeKindEnum;
  units: string;
  category: string;
  projection: CensoredProjectionEnum;
}

function DefinitionFields({
  value,
  onChange,
  allowProfile,
}: {
  value: Definition;
  onChange: (next: Definition) => void;
  allowProfile: boolean;
}) {
  const needsUnit =
    value.kind === AttributeKindEnum.SINGLE_NUMBER || value.kind === AttributeKindEnum.TABULAR_DATA;
  return (
    <div className="grid grid-cols-5 gap-2">
      <Field label="Name">
        <Input
          className="h-7"
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
        />
      </Field>
      <Field label="Kind">
        <SimpleSelect
          value={value.kind}
          options={KIND_OPTIONS.filter(
            (o) => allowProfile || o.value !== AttributeKindEnum.TABULAR_DATA,
          )}
          onChange={(v) => onChange({ ...value, kind: v as AttributeKindEnum })}
        />
      </Field>
      <Field label={needsUnit ? "Units, first is primary" : "Units"}>
        <Input
          className="h-7 font-mono text-xs"
          placeholder="mg/kg, mg/L"
          value={value.units}
          onChange={(e) => onChange({ ...value, units: e.target.value })}
        />
      </Field>
      <Field label="Category">
        <Input
          className="h-7"
          placeholder="Contaminants"
          value={value.category}
          onChange={(e) => onChange({ ...value, category: e.target.value })}
        />
      </Field>
      <Field label="Below limit counts as">
        <SimpleSelect
          value={value.projection}
          options={PROJECTION_OPTIONS}
          onChange={(v) => onChange({ ...value, projection: v as CensoredProjectionEnum })}
        />
      </Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-olive-9">{label}</span>
      {children}
    </label>
  );
}

function definitionOf(entry: Entry): Definition {
  return {
    name: entry.name,
    kind: entry.kind,
    units: entry.units.join(", "),
    category: entry.category ?? "",
    projection: entry.censored_projection,
  };
}

function definitionValid(d: Definition): boolean {
  const needsUnit =
    d.kind === AttributeKindEnum.SINGLE_NUMBER || d.kind === AttributeKindEnum.TABULAR_DATA;
  return d.name.trim() !== "" && (!needsUnit || parseUnits(d.units).length > 0);
}

/** Templates that would stop this definition being asserted. */
function OffenderList({
  offenders,
}: {
  offenders: RouterOutputs["tests"]["attributeCodes"]["bindOffenders"] | undefined;
}) {
  if (!offenders || offenders.length === 0) return null;
  return (
    <div className="border border-red-6 bg-red-2 px-2 py-1 text-xs text-red-11">
      <div className="font-medium">
        Bound templates that do not fit this definition. Fix them on the template first:
      </div>
      <ul className="list-disc pl-4">
        {offenders.map((o, i) => (
          <li key={i}>
            {o.template} → {o.attribute} ({o.kind}
            {o.unit ? `, ${o.unit}` : ""})
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Editing a catalogued result. Name and category save straight away; kind,
 * units and the below-limit rule change what every bound template and every
 * recorded measurement means, so those go through a confirmation that shows
 * the blast radius and the same binder check the server will run.
 */
function EditForm({ entry, onDone }: { entry: Entry; onDone: () => void }) {
  const [draft, setDraft] = useState<Definition>(definitionOf(entry));
  const [nutrient, setNutrient] = useState<FoodNutrientTypeId | null>(entry.food_nutrient_type_id);
  const [confirming, setConfirming] = useState(false);
  const { data: nutrientTypes = [] } = trpc.food.nutrientTypes.list.useQuery();

  const parsedUnits = parseUnits(draft.units);
  const identityChanged =
    draft.kind !== entry.kind ||
    parsedUnits.join("|") !== entry.units.join("|") ||
    draft.projection !== entry.censored_projection;

  const { data: offenders } = trpc.tests.attributeCodes.bindOffenders.useQuery(
    { code: entry.code, kind: draft.kind, units: parsedUnits },
    { enabled: identityChanged },
  );

  const update = trpc.tests.attributeCodes.update.useMutation({
    onSuccess: () => {
      toast.success(`${entry.code} saved`);
      onDone();
    },
    onError: (err) => toast.error(err.message),
  });

  const save = () =>
    update.mutate({
      code: entry.code,
      name: draft.name.trim(),
      kind: draft.kind,
      units: parsedUnits,
      category: draft.category.trim() || null,
      censored_projection: draft.projection,
      food_nutrient_type_id: draft.kind === AttributeKindEnum.SINGLE_NUMBER ? nutrient : null,
    });

  const changed =
    JSON.stringify(draft) !== JSON.stringify(definitionOf(entry)) || nutrient !== entry.food_nutrient_type_id;

  return (
    <div className="flex flex-col gap-2">
      <DefinitionFields
        value={draft}
        onChange={setDraft}
        allowProfile={entry.kind === AttributeKindEnum.TABULAR_DATA}
      />
      {draft.kind === AttributeKindEnum.SINGLE_NUMBER && (
        <div className="grid grid-cols-5 gap-2">
          <Field label="Food nutrient">
            <SimpleSelect<FoodNutrientTypeId | null>
              value={nutrient}
              options={[
                { value: null, label: "Not a nutrient" },
                ...nutrientTypes.map((t) => ({ value: t.id, label: t.name })),
              ]}
              onChange={(v) => setNutrient(v as FoodNutrientTypeId | null)}
            />
          </Field>
          <p className="col-span-4 self-end pb-1.5 text-xs text-olive-9">
            A Farmless batch's latest result for this is its nutrition in recipes, per 100 g.
          </p>
        </div>
      )}
      {identityChanged && <OffenderList offenders={offenders} />}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!changed || !definitionValid(draft) || update.isPending}
          onClick={() => (identityChanged ? setConfirming(true) : save())}
        >
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        {identityChanged && (
          <span className="text-xs text-olive-9">
            Changes what this result is; you will be asked to confirm.
          </span>
        )}
      </div>
      <ConfirmActionDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Redefine ${entry.name}?`}
        description="Kind, units or the below-limit rule are changing. Every template bound to this result and every measurement already recorded will be read under the new definition."
        confirmLabel="Redefine"
        blocked={(offenders?.length ?? 0) > 0}
        isPending={update.isPending}
        onConfirm={() => {
          setConfirming(false);
          save();
        }}
      >
        <div className="flex flex-col gap-2">
          <UsageList entry={entry} />
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
            {draft.kind !== entry.kind && (
              <>
                <dt className="text-olive-9">Kind</dt>
                <dd>
                  {kindLabel(entry.kind)} → {kindLabel(draft.kind)}
                </dd>
              </>
            )}
            {parsedUnits.join("|") !== entry.units.join("|") && (
              <>
                <dt className="text-olive-9">Units</dt>
                <dd className="font-mono">
                  {entry.units.join(", ") || "none"} → {parsedUnits.join(", ") || "none"}
                </dd>
              </>
            )}
            {draft.projection !== entry.censored_projection && (
              <>
                <dt className="text-olive-9">Below limit</dt>
                <dd>
                  {projectionLabel(entry.censored_projection)} →{" "}
                  {projectionLabel(draft.projection)}
                </dd>
              </>
            )}
          </dl>
          <OffenderList offenders={offenders} />
        </div>
      </ConfirmActionDialog>
    </div>
  );
}

/**
 * Adding to the catalogue: a code plus its definition. The code may be one a
 * template already uses in its formulas — then this is the step that turns the
 * template's private name into a shared result, and the binder check applies.
 */
function DefineForm({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState("");
  const [draft, setDraft] = useState<Definition>({
    name: "",
    kind: AttributeKindEnum.SINGLE_NUMBER,
    units: "",
    category: "",
    projection: CensoredProjectionEnum.NULL_VALUE,
  });
  const define = trpc.tests.attributeCodes.define.useMutation({
    onSuccess: (created) => {
      toast.success(`${created.code} added to the catalogue`);
      onDone();
    },
    onError: (err) => toast.error(err.message),
  });
  const codeOk = /^[a-z0-9_]+$/.test(code);
  const { data: existing } = trpc.tests.attributeCodes.get.useQuery(code, { enabled: codeOk });
  const alreadyCatalogued = existing?.visibility === AttributeCodeVisibilityEnum.PUBLIC;
  const templateOnly = existing != null && !alreadyCatalogued;
  const parsedUnits = parseUnits(draft.units);
  const { data: offenders } = trpc.tests.attributeCodes.bindOffenders.useQuery(
    { code, kind: draft.kind, units: parsedUnits },
    { enabled: templateOnly },
  );
  const blocked = alreadyCatalogued || (offenders?.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium">Add to catalogue</div>
      <div className="text-xs text-olive-11">
        Define something a test can report. Measurements themselves are recorded on tests and
        imported from lab reports, never here.
      </div>
      <div className="grid grid-cols-5 gap-2">
        <Field label="Code, typed into formulas">
          <Input
            className="h-7 font-mono"
            placeholder="histamine"
            value={code}
            onChange={(e) => setCode(e.target.value.trim())}
          />
        </Field>
        {alreadyCatalogued && (
          <span className="col-span-4 self-end text-xs text-red-11">
            Already in the catalogue as {existing.name}.
          </span>
        )}
        {templateOnly && (
          <span className="col-span-4 self-end text-xs text-olive-11">
            Templates already use this code as "{existing.name}". Adding it makes every
            one of them report this result.
          </span>
        )}
      </div>
      <DefinitionFields value={draft} onChange={setDraft} allowProfile />
      {templateOnly && <OffenderList offenders={offenders} />}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!codeOk || blocked || !definitionValid(draft) || define.isPending}
          onClick={() =>
            define.mutate({
              code,
              name: draft.name.trim(),
              kind: draft.kind,
              units: parsedUnits,
              category: draft.category.trim() || null,
              censored_projection: draft.projection,
            })
          }
        >
          Add to catalogue
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        {code !== "" && !codeOk && (
          <span className="text-xs text-red-11">
            Lowercase letters, digits and underscores only.
          </span>
        )}
      </div>
    </div>
  );
}

/** The controlled row vocabulary of a profile table. */
function RowKeyList({ code }: { code: string }) {
  const utils = trpc.useUtils();
  const { data: rowKeys } = trpc.tests.attributeCodes.listRowKeys.useQuery({
    code,
    includeArchived: true,
  });
  const invalidate = () => {
    utils.tests.attributeCodes.listRowKeys.invalidate({ code, includeArchived: true });
    utils.tests.attributeCodes.list.invalidate();
  };
  const upsert = trpc.tests.attributeCodes.upsertRowKey.useMutation({
    onSuccess: invalidate,
    onError: (err) => toast.error(err.message),
  });
  const archive = trpc.tests.attributeCodes.setRowKeyArchived.useMutation({
    onSuccess: invalidate,
    onError: (err) => toast.error(err.message),
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ key: "", name: "" });

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs text-olive-9">
        Rows of this profile. Formulas address a row by its key; lab names map onto one.
        {!adding && (
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setAdding(true)}>
            <PlusIcon />
            Add row
          </Button>
        )}
      </div>
      <div className="grid grid-cols-[10rem_1fr_auto] gap-x-2 gap-y-0.5 text-xs">
        {(rowKeys ?? []).map((k) =>
          editingId === k.id ? (
            <RowKeyEditor
              key={k.id}
              initial={{ key: k.key, name: k.name }}
              keyLocked
              pending={upsert.isPending}
              onCancel={() => setEditingId(null)}
              onSave={(v) =>
                upsert.mutate(
                  { code, key: v.key, name: v.name, sort_order: k.sort_order },
                  { onSuccess: () => setEditingId(null) },
                )
              }
            />
          ) : (
            <Fragment key={k.id}>
              <span className={cn("font-mono text-olive-12", k.archived_at && "opacity-50")}>
                {k.key}
              </span>
              <span className={cn(k.archived_at && "opacity-50")}>{k.name}</span>
              <span className="flex items-center gap-0.5">
                {!k.archived_at && (
                  <Button size="sm" variant="ghost" title="Edit" onClick={() => setEditingId(k.id)}>
                    <PencilIcon className="size-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  title={k.archived_at ? "Restore" : "Archive"}
                  onClick={() =>
                    archive.mutate({
                      id: k.id as TestAttributeCodeRowKeyId,
                      archived: k.archived_at == null,
                    })
                  }
                >
                  {k.archived_at ? (
                    <ArchiveRestoreIcon className="size-3.5" />
                  ) : (
                    <ArchiveIcon className="size-3.5" />
                  )}
                </Button>
              </span>
            </Fragment>
          ),
        )}
        {adding && (
          <RowKeyEditor
            initial={draft}
            pending={upsert.isPending}
            onCancel={() => {
              setAdding(false);
              setDraft({ key: "", name: "" });
            }}
            onSave={(v) =>
              upsert.mutate(
                { code, key: v.key, name: v.name },
                {
                  onSuccess: () => {
                    setAdding(false);
                    setDraft({ key: "", name: "" });
                  },
                },
              )
            }
          />
        )}
      </div>
    </div>
  );
}

function RowKeyEditor({
  initial,
  keyLocked = false,
  pending,
  onSave,
  onCancel,
}: {
  initial: { key: string; name: string };
  keyLocked?: boolean;
  pending: boolean;
  onSave: (v: { key: string; name: string }) => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  const ok = v.key.trim() !== "" && v.name.trim() !== "";
  return (
    <>
      <Input
        className="h-7 font-mono"
        placeholder="row_key"
        value={v.key}
        disabled={keyLocked}
        onChange={(e) => setV((d) => ({ ...d, key: e.target.value }))}
      />
      <Input
        className="h-7"
        placeholder="Row name"
        value={v.name}
        onChange={(e) => setV((d) => ({ ...d, name: e.target.value }))}
        onKeyDown={(e) => {
          if (e.key === "Enter" && ok) onSave({ key: v.key.trim(), name: v.name.trim() });
          if (e.key === "Escape") onCancel();
        }}
      />
      <span className="flex items-center gap-0.5">
        <Button
          size="sm"
          disabled={!ok || pending}
          onClick={() => onSave({ key: v.key.trim(), name: v.name.trim() })}
        >
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </span>
    </>
  );
}

/** How each lab spells this result. */
function AliasList({ code, isProfile }: { code: string; isProfile: boolean }) {
  const utils = trpc.useUtils();
  const { data: aliases } = trpc.tests.attributeCodes.listAliases.useQuery({ code });
  const { data: partners } = trpc.tests.externalPartners.list.useQuery();
  const { data: rowKeys } = trpc.tests.attributeCodes.listRowKeys.useQuery(
    { code },
    { enabled: isProfile },
  );
  const invalidate = () => {
    utils.tests.attributeCodes.listAliases.invalidate({ code });
    utils.tests.attributeCodes.list.invalidate();
  };
  const upsert = trpc.tests.attributeCodes.upsertAlias.useMutation({
    onSuccess: invalidate,
    onError: (err) => toast.error(err.message),
  });
  const update = trpc.tests.attributeCodes.updateAlias.useMutation({
    onSuccess: invalidate,
    onError: (err) => toast.error(err.message),
  });
  const remove = trpc.tests.attributeCodes.deleteAlias.useMutation({
    onSuccess: () => {
      toast.success("Lab name removed");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const partnerOptions = (partners ?? []).map((p) => ({ value: String(p.id), label: p.name }));
  const rowKeyOptions = [
    { value: "", label: "— row —" },
    ...(rowKeys ?? []).map((k) => ({ value: k.key, label: k.name })),
  ];
  const rowName = (key: string | null) =>
    key ? (rowKeys?.find((k) => k.key === key)?.name ?? key) : "";

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs text-olive-9">
        How each lab names this result on its reports. Lab names are usually learnt while
        reviewing an import; correct them here.
        {!adding && (
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setAdding(true)}>
            <PlusIcon />
            Add lab name
          </Button>
        )}
      </div>
      <div
        className={cn(
          "grid gap-x-2 gap-y-0.5 text-xs items-center",
          isProfile
            ? "grid-cols-[7rem_1fr_10rem_6rem_6rem_auto]"
            : "grid-cols-[7rem_1fr_6rem_6rem_auto]",
        )}
      >
        {(aliases ?? []).map((alias) =>
          editingId === alias.id ? (
            <AliasEditor
              key={alias.id}
              isProfile={isProfile}
              partnerOptions={partnerOptions}
              rowKeyOptions={rowKeyOptions}
              partnerLocked
              initial={{
                partnerId: String(alias.external_test_partner_id),
                labName: alias.lab_parameter_name,
                labUnit: alias.lab_unit ?? "",
                rowKey: alias.row_key ?? "",
                factor: alias.unit_factor ?? "",
              }}
              pending={update.isPending}
              onCancel={() => setEditingId(null)}
              onSave={(v) =>
                update.mutate(
                  {
                    id: alias.id as TestAttributeCodeAliasId,
                    lab_parameter_name: v.labName,
                    lab_unit: v.labUnit || null,
                    row_key: isProfile ? v.rowKey || null : null,
                    unit_factor: v.factor || null,
                  },
                  { onSuccess: () => setEditingId(null) },
                )
              }
            />
          ) : (
            <Fragment key={alias.id}>
              <span className="text-olive-11">{alias.partner_name}</span>
              <span>{alias.lab_parameter_name}</span>
              {isProfile && <span className="text-olive-11">{rowName(alias.row_key)}</span>}
              <span className="font-mono">{alias.lab_unit ?? ""}</span>
              <span className="font-mono text-olive-11">
                {alias.unit_factor ? `× ${alias.unit_factor}` : ""}
              </span>
              <span className="flex items-center gap-0.5 justify-end">
                <Button size="sm" variant="ghost" title="Edit" onClick={() => setEditingId(alias.id)}>
                  <PencilIcon className="size-3.5" />
                </Button>
                <DeleteConfirmDialog
                  title={`Remove lab name "${alias.lab_parameter_name}"?`}
                  description={`The next ${alias.partner_name} report will ask again what this parameter is. Measurements already imported are kept.`}
                  deleteLabel="Remove"
                  onConfirm={() => remove.mutate(alias.id as TestAttributeCodeAliasId)}
                  trigger={
                    <Button size="sm" variant="ghost" title="Remove">
                      <Trash2Icon className="size-3.5 text-red-11" />
                    </Button>
                  }
                />
              </span>
            </Fragment>
          ),
        )}
        {adding && (
          <AliasEditor
            isProfile={isProfile}
            partnerOptions={partnerOptions}
            rowKeyOptions={rowKeyOptions}
            initial={{ partnerId: "", labName: "", labUnit: "", rowKey: "", factor: "" }}
            pending={upsert.isPending}
            onCancel={() => setAdding(false)}
            onSave={(v) =>
              upsert.mutate(
                {
                  external_test_partner_id: Number(v.partnerId) as ExternalTestPartnerId,
                  lab_parameter_name: v.labName,
                  lab_unit: v.labUnit || null,
                  attribute_code: code,
                  row_key: isProfile ? v.rowKey : null,
                  unit_factor: v.factor || null,
                },
                { onSuccess: () => setAdding(false) },
              )
            }
          />
        )}
      </div>
    </div>
  );
}

interface AliasDraft {
  partnerId: string;
  labName: string;
  labUnit: string;
  rowKey: string;
  factor: string;
}

function AliasEditor({
  isProfile,
  partnerOptions,
  rowKeyOptions,
  partnerLocked = false,
  initial,
  pending,
  onSave,
  onCancel,
}: {
  isProfile: boolean;
  partnerOptions: { value: string; label: string }[];
  rowKeyOptions: { value: string; label: string }[];
  partnerLocked?: boolean;
  initial: AliasDraft;
  pending: boolean;
  onSave: (v: AliasDraft) => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  const ok = v.partnerId !== "" && v.labName.trim() !== "" && (!isProfile || v.rowKey !== "");
  const commit = () =>
    onSave({
      ...v,
      labName: v.labName.trim(),
      labUnit: v.labUnit.trim(),
      factor: v.factor.trim(),
    });
  return (
    <>
      {partnerLocked ? (
        <span className="text-olive-11">
          {partnerOptions.find((p) => p.value === v.partnerId)?.label}
        </span>
      ) : (
        <SimpleSelect
          value={v.partnerId}
          options={partnerOptions}
          placeholder="Lab…"
          onChange={(p) => setV((d) => ({ ...d, partnerId: String(p ?? "") }))}
        />
      )}
      <Input
        className="h-7"
        placeholder="Lab's parameter name"
        value={v.labName}
        onChange={(e) => setV((d) => ({ ...d, labName: e.target.value }))}
        onKeyDown={(e) => {
          if (e.key === "Enter" && ok) commit();
          if (e.key === "Escape") onCancel();
        }}
      />
      {isProfile && (
        <SimpleSelect
          value={v.rowKey}
          options={rowKeyOptions}
          onChange={(k) => setV((d) => ({ ...d, rowKey: String(k ?? "") }))}
        />
      )}
      <Input
        className="h-7"
        placeholder="lab unit"
        value={v.labUnit}
        onChange={(e) => setV((d) => ({ ...d, labUnit: e.target.value }))}
      />
      <Input
        className="h-7 font-mono"
        placeholder="× factor"
        value={v.factor}
        onChange={(e) => setV((d) => ({ ...d, factor: e.target.value }))}
      />
      <span className="flex items-center gap-0.5 justify-end">
        <Button size="sm" disabled={!ok || pending} onClick={commit}>
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </span>
    </>
  );
}

function Th({
  children,
  className,
  title,
}: {
  children?: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <th
      title={title}
      className={cn(
        "border-b border-olive-6 px-2 py-1.5 text-left text-xs font-medium text-olive-11",
        className,
      )}
    >
      {children}
    </th>
  );
}
